// src/components/pos/POSProducts.js
import { useRef } from 'react'
import { UNIT_TYPES, POS_MODES } from './pos.constants'

function Ic({ path, size = 14, color = 'currentColor', sw = 1.8, paths }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      {paths ? paths.map((p, i) => <path key={i} d={p} />) : <path d={path} />}
    </svg>
  )
}

function StockBadge({ stock, isService }) {
  if (isService) return <span style={{ fontSize:'10px', fontWeight:700, color:'var(--primary)', background:'var(--primary-light)', padding:'1px 6px', borderRadius:'var(--radius-full)' }}>Service</span>
  if (stock === 0) return <span style={{ fontSize:'10px', fontWeight:700, color:'var(--error)', background:'#FEF2F2', padding:'1px 6px', borderRadius:'var(--radius-full)' }}>Out of stock</span>
  if (stock <= 5)  return <span style={{ fontSize:'10px', fontWeight:700, color:'var(--warning)', background:'#FFF8E6', padding:'1px 6px', borderRadius:'var(--radius-full)' }}>Low: {stock}</span>
  return <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>{stock} left</span>
}

function ProductTile({ product, onAdd, sym, isJewellery, goldRates, posMode }) {
  const outOfStock = !product.is_service && product.stock === 0
  const isWeightMode = ['grocery', 'fresh_food', 'fabric', 'bakery'].includes(posMode) && product.unit_price_per
  const unitLabel = UNIT_TYPES[product.unit_type]?.short || product.unit_type || 'pc'

  // Jewellery: show gold info instead of fixed price
  const showGoldPrice = isJewellery && product.gold_karat && goldRates
  const karat = product.gold_karat || ''
  const rateKey = karat === 'Silver 925' || karat === 'Silver 999' ? 'rate_silver' : `rate_${karat.toLowerCase().replace('k','').replace(' ','_')}k`
  const goldRate = goldRates?.[rateKey] || 0
  const estimatedPrice = showGoldPrice && product.gold_weight
    ? Math.round(product.gold_weight * goldRate + (product.stone_value || 0))
    : null

  return (
    <button onClick={() => !outOfStock && onAdd(product)}
      disabled={outOfStock}
      style={{
        padding:'10px', borderRadius:'var(--radius-md)', textAlign:'left', cursor: outOfStock ? 'not-allowed' : 'pointer',
        border: product.is_pinned ? '2px solid var(--primary)' : '1px solid var(--border)',
        background: product.is_pinned ? 'var(--primary-light)' : outOfStock ? 'var(--bg-muted)' : 'var(--bg-card)',
        opacity: outOfStock ? 0.55 : 1, display:'flex', flexDirection:'column', gap:'6px',
        transition:'all 0.1s', position:'relative', overflow:'hidden',
      }}
      onMouseEnter={e => { if (!outOfStock) { e.currentTarget.style.borderColor='var(--primary)'; e.currentTarget.style.boxShadow='0 4px 12px rgba(11,31,58,0.1)' } }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = product.is_pinned ? 'var(--primary)' : 'var(--border)'; e.currentTarget.style.boxShadow='none' }}>

      {/* Pinned indicator */}
      {product.is_pinned && (
        <div style={{ position:'absolute', top:0, right:0, width:0, height:0, borderStyle:'solid', borderWidth:'0 20px 20px 0', borderColor:`transparent var(--primary) transparent transparent` }} />
      )}

      {/* Product image / colour swatch */}
      <div style={{ width:'100%', aspectRatio:'1/1', borderRadius:'var(--radius-sm)', overflow:'hidden', background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
        ) : product.color ? (
          <div style={{ width:'100%', height:'100%', background: product.color.startsWith('#') ? product.color : undefined, display:'flex', alignItems:'center', justifyContent:'center' }}>
            {!product.color.startsWith('#') && <span style={{ fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', padding:'2px', textAlign:'center', wordBreak:'break-word' }}>{product.color}</span>}
          </div>
        ) : (
          <Ic path="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" color="var(--border)" size={20} sw={1.2} />
        )}
      </div>

      {/* Name */}
      <div style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-primary)', lineHeight:1.3, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
        {product.name}
      </div>

      {/* Jewellery details */}
      {isJewellery && product.gold_karat && (
        <div style={{ fontSize:'10px', fontWeight:700, color:'var(--warning)', background:'#FFF8E6', padding:'2px 6px', borderRadius:'var(--radius-full)', display:'inline-block' }}>
          {product.gold_karat}{product.gold_weight ? ` · ${product.gold_weight}g` : ''}
        </div>
      )}

      {/* Pharmacy: expiry warning */}
      {posMode === 'pharmacy' && product.expiry_date && (
        <div style={{ fontSize:'10px', color: new Date(product.expiry_date) < new Date() ? 'var(--error)' : 'var(--text-muted)' }}>
          Exp: {new Date(product.expiry_date).toLocaleDateString('en-BD', { month:'short', year:'numeric' })}
        </div>
      )}
      {posMode === 'pharmacy' && product.requires_prescription && (
        <span style={{ fontSize:'10px', fontWeight:700, color:'var(--error)', background:'#FEF2F2', padding:'1px 6px', borderRadius:'var(--radius-full)', display:'inline-block' }}>Rx</span>
      )}

      {/* Price row */}
      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginTop:'auto', gap:'4px' }}>
        <div>
          {showGoldPrice && goldRate > 0 ? (
            <div>
              <div style={{ fontSize:'10px', color:'var(--text-muted)' }}>{sym}{goldRate.toLocaleString()}/g</div>
              {estimatedPrice && <div style={{ fontSize:'13px', fontWeight:800, color:'var(--warning)' }}>≈{sym}{estimatedPrice.toLocaleString()}</div>}
            </div>
          ) : isWeightMode ? (
            <div>
              <div style={{ fontSize:'13px', fontWeight:800, color:'var(--primary)' }}>{sym}{Number(product.sell_price || 0).toLocaleString()}</div>
              <div style={{ fontSize:'10px', color:'var(--text-muted)' }}>per {unitLabel}</div>
            </div>
          ) : (
            <div style={{ fontSize:'13px', fontWeight:800, color:'var(--primary)' }}>{sym}{Number(product.sell_price || 0).toLocaleString()}</div>
          )}
        </div>
        <StockBadge stock={product.stock} isService={product.is_service} />
      </div>
    </button>
  )
}

export default function POSProducts({
  products, allProducts, categories, activeCategory, setActiveCategory,
  searchQuery, setSearchQuery,
  cameraOpen, cameraError, scanning, scanFlash,
  videoRef, barcodeRef,
  loading, posMode, goldRates, sym, isJewellery,
  onAddProduct, onBarcodeSubmit, onCameraOpen, onCameraStop,
}) {
  const catRef = useRef(null)

  const handleKey = (e) => {
    if (e.key === 'Enter') onBarcodeSubmit(e)
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>

      {/* ── Search + barcode bar ── */}
      <div style={{ padding:'10px 12px', borderBottom:'1px solid var(--border)', background:'var(--bg-card)', flexShrink:0 }}>
        <div style={{ display:'flex', gap:'7px', alignItems:'center' }}>
          {/* Combined search / barcode input */}
          <div style={{ flex:1, position:'relative' }}>
            <Ic path="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" color="var(--text-muted)" size={14} style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)' }} />
            <div style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </div>
            <input
              ref={barcodeRef}
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Search or scan barcode..."
              autoComplete="off"
              style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'8px 10px 8px 32px', fontSize:'12.5px', outline:'none', background:'var(--bg-card)', color:'var(--text-primary)', fontFamily:'var(--font-ui)', boxSizing:'border-box', transition:'border-color 0.15s' }}
              onFocus={e => e.target.style.borderColor='var(--primary)'}
              onBlur={e => e.target.style.borderColor='var(--border)'}
            />
          </div>

          {/* Camera scan button */}
          <button onClick={cameraOpen ? onCameraStop : onCameraOpen}
            style={{ width:'36px', height:'36px', borderRadius:'var(--radius-md)', border:'1px solid', borderColor: cameraOpen ? 'var(--error)' : 'var(--border)', background: cameraOpen ? '#FEF2F2' : 'var(--bg-card)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cameraOpen ? 'var(--error)' : 'var(--text-muted)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {cameraOpen
                ? <path d="M6 18L18 6M6 6l12 12"/>
                : <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z M12 17a4 4 0 100-8 4 4 0 000 8z"/>
              }
            </svg>
          </button>

          {/* Clear search */}
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}
              style={{ width:'36px', height:'36px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          )}
        </div>

        {/* Camera viewfinder */}
        {cameraOpen && (
          <div style={{ marginTop:'8px', position:'relative', borderRadius:'var(--radius-md)', overflow:'hidden', background:'#000', lineHeight:0 }}>
            <video ref={videoRef} muted playsInline style={{ width:'100%', maxHeight:'180px', objectFit:'cover', display:'block' }} />
            <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
              {/* Corner brackets */}
              {[
                { top:'15%', left:'10%',  borderTop:'3px solid', borderLeft:'3px solid' },
                { top:'15%', right:'10%', borderTop:'3px solid', borderRight:'3px solid' },
                { bottom:'15%', left:'10%',  borderBottom:'3px solid', borderLeft:'3px solid' },
                { bottom:'15%', right:'10%', borderBottom:'3px solid', borderRight:'3px solid' },
              ].map((s, i) => <div key={i} style={{ position:'absolute', width:'20px', height:'20px', borderColor: scanFlash ? '#22C55E' : '#fff', opacity:0.9, ...s }} />)}
              {/* Scan line */}
              <div style={{ position:'absolute', left:'10%', right:'10%', height:'2px', background:'linear-gradient(to right, transparent, var(--primary), transparent)', animation:'pos-scanline 1.5s ease-in-out infinite' }} />
            </div>
            {scanFlash && <div style={{ position:'absolute', inset:0, background:'rgba(34,197,94,0.3)', pointerEvents:'none' }} />}
            <div style={{ position:'absolute', bottom:'8px', left:0, right:0, textAlign:'center', pointerEvents:'none' }}>
              <span style={{ fontSize:'11px', fontWeight:600, color:'#fff', background:'rgba(0,0,0,0.6)', padding:'3px 10px', borderRadius:'var(--radius-full)' }}>
                {scanning ? 'Scanning...' : 'Point at barcode'}
              </span>
            </div>
          </div>
        )}
        {cameraError && (
          <div style={{ marginTop:'6px', fontSize:'11.5px', color:'var(--warning)', background:'#FFF8E6', padding:'7px 10px', borderRadius:'var(--radius-sm)', border:'1px solid #FDE68A' }}>{cameraError}</div>
        )}
      </div>

      {/* ── Category tabs ── */}
      <div ref={catRef} style={{ display:'flex', gap:'4px', padding:'8px 12px', overflowX:'auto', borderBottom:'1px solid var(--border)', background:'var(--bg-card)', flexShrink:0, scrollbarWidth:'none' }}>
        {categories.map(cat => (
          <button key={cat} onClick={() => setActiveCategory(cat)}
            style={{ padding:'5px 12px', borderRadius:'var(--radius-full)', border:'1.5px solid', borderColor: activeCategory === cat ? 'var(--primary)' : 'var(--border)', background: activeCategory === cat ? 'var(--primary)' : 'var(--bg-card)', color: activeCategory === cat ? '#fff' : 'var(--text-muted)', fontSize:'11.5px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0, transition:'all 0.1s' }}>
            {cat === 'Pinned' ? '📌 Pinned' : cat}
            {cat !== 'All' && cat !== 'Pinned' && (
              <span style={{ marginLeft:'4px', opacity:0.6 }}>
                ({allProducts.filter(p => p.category === cat).length})
              </span>
            )}
            {cat === 'All' && <span style={{ marginLeft:'4px', opacity:0.6 }}>({allProducts.length})</span>}
            {cat === 'Pinned' && <span style={{ marginLeft:'4px', opacity:0.6 }}>({allProducts.filter(p => p.is_pinned).length})</span>}
          </button>
        ))}
      </div>

      {/* ── Product grid ── */}
      <div style={{ flex:1, overflowY:'auto', padding:'10px 12px' }}>
        {loading ? (
          <div style={{ padding:'48px', textAlign:'center' }}>
            <div style={{ width:'24px', height:'24px', borderRadius:'50%', border:'2.5px solid var(--primary)', borderTopColor:'transparent', animation:'pos-spin 0.7s linear infinite', margin:'0 auto 12px' }} />
            <div style={{ fontSize:'13px', color:'var(--text-muted)' }}>Loading products...</div>
          </div>
        ) : products.length === 0 ? (
          <div style={{ padding:'48px', textAlign:'center' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.2" strokeLinecap="round" style={{ margin:'0 auto 12px', display:'block' }}><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
            <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', marginBottom:'4px' }}>
              {searchQuery ? 'No products match your search' : 'No products in this category'}
            </div>
            <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>
              {searchQuery ? 'Try a different search term or scan a barcode' : 'Add products in Inventory to see them here'}
            </div>
          </div>
        ) : (
          <div className="pos-product-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'8px' }}>
            {products.map(p => (
              <ProductTile
                key={p.id} product={p}
                onAdd={onAddProduct} sym={sym}
                isJewellery={isJewellery} goldRates={goldRates} posMode={posMode}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Mode indicator footer ── */}
      <div style={{ padding:'6px 12px', borderTop:'1px solid var(--border)', background:'var(--bg-muted)', display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d={POS_MODES[posMode]?.icon || POS_MODES.standard.icon}/>
        </svg>
        <span style={{ fontSize:'10.5px', color:'var(--text-muted)', fontWeight:600 }}>
          {POS_MODES[posMode]?.label || 'Standard'} mode
        </span>
        <span style={{ fontSize:'10.5px', color:'var(--text-muted)', marginLeft:'auto' }}>
          {products.length} product{products.length !== 1 ? 's' : ''} shown
        </span>
      </div>

      <style>{`
        @keyframes pos-spin { to { transform: rotate(360deg) } }
        @keyframes pos-scanline { 0%{top:20%} 50%{top:75%} 100%{top:20%} }
        .pos-product-grid { display: grid !important; }
        @media(max-width:1400px) { .pos-product-grid { grid-template-columns: repeat(3, 1fr) !important; } }
        @media(max-width:900px)  { .pos-product-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>
    </div>
  )
}